import type { Location } from '@/types/location';
import { getLocationLifecycleStatus, isLocationArchived } from '@/lib/location-lifecycle';

export type PinnedCustomerOpsAccount = {
  accountKey: string;
  accountLabel: string;
  description: string;
  locationNumbers: readonly string[];
  primaryLocationNumber: string;
  /** Phase 1 reference account — green accent on hub card. */
  isReference?: boolean;
};

/** Multi-store portfolios aligned with scripts/scope-reporting-customers.config.ts */
export const CUSTOMER_OPS_PORTFOLIO_ACCOUNTS: readonly PinnedCustomerOpsAccount[] = [
  {
    accountKey: 'lazy-river',
    accountLabel: 'Lazy River',
    description: 'Phase 1 reference customer (LOC-040 Dracut · LOC-041 Tewksbury)',
    locationNumbers: ['LOC-040', 'LOC-041'],
    primaryLocationNumber: 'LOC-040',
    isReference: true,
  },
  {
    accountKey: 'budnrita',
    accountLabel: 'Bud N Rita',
    description: 'Avondale · Forsyth · Niles · Wadsworth',
    locationNumbers: ['LOC-007', 'LOC-008', 'LOC-009', 'LOC-010'],
    primaryLocationNumber: 'LOC-007',
  },
  {
    accountKey: 'cannaplanet',
    accountLabel: 'Canna Planet',
    description: 'Budd Barn · Budds · Ellenville · Kingston · Liberty · Monticello · Newburg · Wappingers',
    locationNumbers: [
      'LOC-005',
      'LOC-006',
      'LOC-012',
      'LOC-013',
      'LOC-Canna-Planet-Liberty',
      'LOC-014',
      'LOC-015',
      'LOC-016',
    ],
    primaryLocationNumber: 'LOC-012',
  },
  {
    accountKey: 'ccc',
    accountLabel: 'CCC',
    description: 'California portfolio incl. Klutch Akron (LOC-020)',
    locationNumbers: ['LOC-017', 'LOC-018', 'LOC-019', 'LOC-020', 'LOC-021', 'LOC-022', 'LOC-023', 'LOC-024'],
    primaryLocationNumber: 'LOC-017',
  },
  {
    accountKey: 'high-season',
    accountLabel: 'High Season',
    description: 'Adelanto · Fontana',
    locationNumbers: ['LOC-030', 'LOC-031'],
    primaryLocationNumber: 'LOC-031',
  },
  {
    accountKey: 'klutch',
    accountLabel: 'Klutch',
    description: 'Canton · Cleveland · Lorain',
    locationNumbers: ['LOC-037', 'LOC-038', 'LOC-039'],
    primaryLocationNumber: 'LOC-039',
  },
  {
    accountKey: 'ngw-okay',
    accountLabel: 'NGW / OKAY',
    description: 'Peoria Glen · University · Evanston · West Town · LOC-053',
    locationNumbers: ['LOC-042', 'LOC-043', 'LOC-044', 'LOC-045', 'LOC-053'],
    primaryLocationNumber: 'LOC-042',
  },
] as const;

function slugAccountKey(location: Location): string {
  return location.location_number.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function isFleetTrackedLocation(location: Location): boolean {
  return !isLocationArchived(location) && getLocationLifecycleStatus(location) === 'active';
}

/** All active Customer Ops account groups — portfolios plus single-store locations not in a portfolio. */
export function buildFleetCustomerOpsAccounts(locations: Location[]): PinnedCustomerOpsAccount[] {
  const byNumber = new Map(locations.map((loc) => [loc.location_number, loc]));
  const assignedNumbers = new Set<string>();
  const accounts: PinnedCustomerOpsAccount[] = [];

  for (const portfolio of CUSTOMER_OPS_PORTFOLIO_ACCOUNTS) {
    const activeNumbers = portfolio.locationNumbers.filter((number) => {
      const loc = byNumber.get(number);
      return loc && isFleetTrackedLocation(loc);
    });

    if (activeNumbers.length === 0) continue;

    activeNumbers.forEach((number) => assignedNumbers.add(number));
    const primary =
      activeNumbers.includes(portfolio.primaryLocationNumber)
        ? portfolio.primaryLocationNumber
        : activeNumbers[0]!;

    accounts.push({
      ...portfolio,
      locationNumbers: activeNumbers,
      primaryLocationNumber: primary,
    });
  }

  const singles = locations
    .filter(isFleetTrackedLocation)
    .filter((loc) => !assignedNumbers.has(loc.location_number))
    .sort((a, b) => a.location_name.localeCompare(b.location_name));

  for (const location of singles) {
    accounts.push({
      accountKey: slugAccountKey(location),
      accountLabel: location.location_name,
      description: `${location.location_number} — single-store fleet account`,
      locationNumbers: [location.location_number],
      primaryLocationNumber: location.location_number,
    });
  }

  return accounts.sort((a, b) => {
    if (a.isReference) return -1;
    if (b.isReference) return 1;
    return a.accountLabel.localeCompare(b.accountLabel);
  });
}

export function getFleetLocationNumbers(locations: Location[]): string[] {
  return buildFleetCustomerOpsAccounts(locations).flatMap((account) => [...account.locationNumbers]);
}
